import mapboxgl from 'mapbox-gl';
import moment from 'moment';
import {
  getFeatureAgentLabel,
  AGENT_LABEL_TEXT_FIELD
} from '../../../../utils/agentUtil';
import { haversineMeters } from '../../../../utils/locationUtil';

export function setActualTrackLayer(mapLayer) {
  const trackVisibility = this.props.selectedLayers.includes(mapLayer.name)
    ? 'visible'
    : 'none';
  const trackLineFilter = [
    'any',
    ['==', ['geometry-type'], 'LineString'],
    ['==', ['geometry-type'], 'MultiLineString']
  ];
  const defaultTrackColor =
    this.props.trackColor === 'blue' ? '#0096FF' : '#FFAB00';
  // 'colourCoded' tags each line segment/point with its own hour-of-day
  // colour (see actualTracks.js, reusing tracksFullColour.toColouredSegments)
  // - coalesce prefers that over the flat focusColor/color/default so the
  // gradient actually shows; untimed fallback segments have no hourColor and
  // simply fall through to the existing behaviour.
  const trackColor = [
    'coalesce',
    ['get', 'hourColor'],
    ['get', 'focusColor'],
    ['get', 'color'],
    defaultTrackColor
  ];
  // Many short per-hour segments replace the one line-per-track feature in
  // colour-coded mode (see actualTracks.js) - symbol-placement spacing is
  // evaluated per feature, so this would otherwise crowd the map with a
  // direction arrow on every short segment. 'Full tracks - colour coded'
  // has never had direction arrows either; hide them here too rather than
  // add clutter.
  const trackDirectionVisibility =
    this.props.trackColor === 'colourCoded' ? 'none' : trackVisibility;

  if (!this.map.getLayer('actualTracks_outline')) {
    this.map.addLayer({
      id: 'actualTracks_outline',
      source: 'actualTracks',
      type: 'line',
      filter: trackLineFilter,
      layout: { visibility: trackVisibility },
      paint: {
        'line-color': 'rgba(0,0,0,0.6)',
        'line-width': 5,
        'line-opacity': 1
      }
    });
  }

  if (!this.map.getLayer('actualTracks')) {
    this.map.addLayer({
      id: 'actualTracks',
      source: 'actualTracks',
      type: 'line',
      filter: trackLineFilter,
      layout: { visibility: trackVisibility },
      paint: {
        'line-color': trackColor,
        'line-width': 3,
        'line-opacity': 1
      }
    });
  } else {
    this.map.setPaintProperty('actualTracks', 'line-color', trackColor);
    this.map.setPaintProperty('actualTracks', 'line-width', 3);
    this.map.setPaintProperty('actualTracks', 'line-opacity', 1);
  }

  if (!this.map.getLayer('actualTracks_points')) {
    this.map.addLayer({
      id: 'actualTracks_points',
      source: 'actualTracks',
      type: 'circle',
      filter: [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['==', ['get', '_tracePoint'], true]
      ],
      layout: { visibility: trackVisibility },
      paint: {
        'circle-radius': 2.25,
        'circle-color': trackColor,
        'circle-opacity': 0.95,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000000'
      }
    });
  }

  if (this.map.getLayer(this.trackDirectionLayerId)) {
    this.map.removeLayer(this.trackDirectionLayerId);
  }
  this.map.addLayer({
    id: this.trackDirectionLayerId,
    source: 'actualTracks',
    type: 'symbol',
    filter: trackLineFilter,
    layout: {
      visibility: trackDirectionVisibility,
      'symbol-placement': 'line',
      'symbol-spacing': 70,
      'text-field': '◀',
      'text-size': ['interpolate', ['linear'], ['zoom'], 1, 13, 22, 22],
      'text-keep-upright': false,
      'text-allow-overlap': true,
      'text-ignore-placement': true
    },
    paint: {
      'text-color': trackColor,
      'text-opacity': 1,
      'text-halo-color': 'rgba(0, 0, 0, 0.8)',
      'text-halo-width': 1.5,
      'text-halo-blur': 0
    }
  });

  if (this.map.getLayer(this.trackEndpointsLayerId)) {
    this.map.removeLayer(this.trackEndpointsLayerId);
  }

  this.map.addLayer({
    id: this.trackEndpointsLayerId,
    source: this.trackEndpointsSourceId,
    type: 'circle',
    layout: {
      visibility: this.props.selectedLayers.includes(mapLayer.name)
        ? 'visible'
        : 'none'
    },
    paint: {
      'circle-radius': 9,
      'circle-color': [
        'match',
        ['get', 'markerKind'],
        'start',
        '#2FA84F',
        'end',
        '#E43A35',
        '#2FA84F'
      ],
      'circle-opacity': 1,
      'circle-stroke-width': 3,
      'circle-stroke-color': '#ffffff'
    }
  });

  if (this.map.getLayer('actualTracks')) {
    this.map.moveLayer('actualTracks');
  }
  if (this.map.getLayer('actualTracks_outline')) {
    this.map.moveLayer('actualTracks_outline', 'actualTracks');
  }
  if (this.map.getLayer(this.trackDirectionLayerId)) {
    this.map.moveLayer(this.trackDirectionLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
    this.map.moveLayer(this.trackPlaybackProgressLayerId + '_outline');
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
    this.map.moveLayer(this.trackPlaybackProgressLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
    this.map.moveLayer(
      this.trackPlaybackProgressLayerId + '_outline',
      this.trackPlaybackProgressLayerId
    );
  }
  if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
    this.map.moveLayer(this.trackPlaybackObservationLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackLayerId)) {
    this.map.moveLayer(this.trackPlaybackLayerId);
  }
  this.bringTrackEndpointsToFront();
  if (this.map.getLayer('Agents')) {
    this.map.moveLayer('Agents');
  }
  if (this.map.getLayer('Agents_zoom_highlight')) {
    this.map.moveLayer('Agents_zoom_highlight');
  }
  this.bringTrackEndpointsToFront();

  if (!this.actualTrackHoverSetup) {
    this.actualTrackHoverSetup = true;

    const trackHoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false
    });

    const normalizeDt = raw =>
      raw ? String(raw).replace(/([+-]\d{2})(\d{2})$/, '$1:$2') : null;

    const parseDt = raw => {
      const s = normalizeDt(raw);
      if (!s) return null;
      const m = moment(s);
      return m.isValid() ? m : null;
    };

    const formatDuration = minutes => {
      if (minutes < 60) return `${Math.round(minutes)} minutes`;
      const h = Math.floor(minutes / 60);
      const m = Math.round(minutes % 60);
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };

    let isHoveringTrack = false;
    let isHoveringPopup = false;

    const hidePopup = () => {
      if (!isHoveringTrack && !isHoveringPopup) {
        this.map.getCanvas().style.cursor = '';
        trackHoverPopup.remove();
      }
    };

    const buildPopupHtml = ({
      name,
      dateTimeLabel,
      timeSinceStartLabel,
      distanceLabel
    }) =>
      `<div style="line-height:1.5;min-width:160px;">
        ${
          name
            ? `<div style="font-weight:600;font-size:14px;margin-bottom:2px;">${name}</div>`
            : ''
        }
        ${
          dateTimeLabel
            ? `<div style="font-size:12px;color:#6E757C;">${dateTimeLabel}</div>`
            : ''
        }
        ${
          timeSinceStartLabel
            ? `<div style="font-size:12px;color:#6E757C;">Duration from start: ${timeSinceStartLabel}</div>`
            : ''
        }
        <div style="font-size:12px;color:#6E757C;">Distance from start: ${distanceLabel}</div>
      </div>`;

    this.map.on('mousemove', 'actualTracks', e => {
      if (!e.features || !e.features.length) return;
      this.map.getCanvas().style.cursor = 'pointer';

      const feature = e.features[0];
      const props = feature.properties || {};

      // DEBUG — remove after diagnosis
      console.log('[trackHover] props keys:', Object.keys(props));
      console.log('[trackHover] _hoverAgentName:', props._hoverAgentName);
      console.log(
        '[trackHover] _hoverDateTimes:',
        props._hoverDateTimes ? props._hoverDateTimes.slice(0, 80) : null
      );
      console.log('[trackHover] selectedItem:', this.props.selectedItem);

      // Agent name — one shared resolver (registration name, else the data's
      // own name, else the raw id), then whatever was promoted into GeoJSON
      // properties for features set from map clicks.
      const sel = this.props.selectedItem;
      const selProps = (sel && sel.properties) || {};
      const name =
        (sel ? getFeatureAgentLabel(sel) : '') || props._hoverAgentName || '';

      // startWhen — selectedItem first (same as TrackItem), then feature property
      const startWhen =
        (sel && sel.startWhen) ||
        selProps.startWhen ||
        selProps.StartWhen ||
        props._hoverStartWhen ||
        null;

      // dateTimes array — promoted as JSON string to _hoverDateTimes
      let dateTimes = [];
      if (props._hoverDateTimes) {
        try {
          const parsed = JSON.parse(props._hoverDateTimes);
          if (Array.isArray(parsed)) dateTimes = parsed;
        } catch (_err) {
          // Ignore JSON parse errors
        }
      }

      // Coordinates from feature geometry
      let coordinates = [];
      const geom = feature.geometry;
      if (
        geom &&
        geom.type === 'LineString' &&
        Array.isArray(geom.coordinates)
      ) {
        coordinates = geom.coordinates;
      } else if (
        geom &&
        geom.type === 'MultiLineString' &&
        Array.isArray(geom.coordinates)
      ) {
        coordinates = geom.coordinates.reduce((acc, line) => {
          if (Array.isArray(line)) acc.push(...line);
          return acc;
        }, []);
      }

      if (!coordinates.length) return;

      // Find nearest coordinate index to mouse position
      const mouseLng = e.lngLat.lng;
      const mouseLat = e.lngLat.lat;
      let nearestIndex = 0;
      let minDistSq = Infinity;
      coordinates.forEach((coord, i) => {
        if (!Array.isArray(coord) || coord.length < 2) return;
        const dx = coord[0] - mouseLng;
        const dy = coord[1] - mouseLat;
        const dSq = dx * dx + dy * dy;
        if (dSq < minDistSq) {
          minDistSq = dSq;
          nearestIndex = i;
        }
      });

      // Calculate cumulative distance from start to nearest point
      let totalMeters = 0;
      for (let i = 1; i <= nearestIndex; i++) {
        const a = coordinates[i - 1];
        const b = coordinates[i];
        if (
          Array.isArray(a) &&
          Array.isArray(b) &&
          a.length >= 2 &&
          b.length >= 2
        ) {
          totalMeters += haversineMeters(a, b);
        }
      }
      const distanceLabel =
        totalMeters >= 1000
          ? `${(totalMeters / 1000).toFixed(2)} km`
          : `${Math.round(totalMeters)} m`;

      // Time elapsed since start of track
      const pointMoment = parseDt(dateTimes[nearestIndex]);
      const dateTimeLabel = pointMoment
        ? pointMoment.format('DD MMM YY HH:mm')
        : '';
      const startMoment =
        parseDt(startWhen) || (dateTimes.length ? parseDt(dateTimes[0]) : null);
      const timeSinceStartLabel =
        startMoment && pointMoment && pointMoment.isAfter(startMoment)
          ? formatDuration(pointMoment.diff(startMoment, 'minutes'))
          : '';

      isHoveringTrack = true;
      trackHoverPopup
        .setLngLat(e.lngLat)
        .setHTML(
          buildPopupHtml({
            name,
            dateTimeLabel,
            timeSinceStartLabel,
            distanceLabel
          })
        )
        .addTo(this.map);

      const popupEl = trackHoverPopup.getElement();
      if (popupEl && !popupEl._trackHoverListened) {
        popupEl._trackHoverListened = true;
        popupEl.addEventListener('mouseenter', () => {
          isHoveringPopup = true;
        });
        popupEl.addEventListener('mouseleave', () => {
          isHoveringPopup = false;
          hidePopup();
        });
      }
    });

    this.map.on('mouseleave', 'actualTracks', () => {
      isHoveringTrack = false;
      hidePopup();
    });
  }
}

export function refreshTrackPlaybackColors() {
  const color = this.props.trackColor === 'blue' ? '#0096FF' : '#FFAB00';
  const pastColorExpr = [
    'match',
    ['get', 'progressState'],
    'past',
    color,
    '#b8bec7'
  ];
  if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
    this.map.setPaintProperty(
      this.trackPlaybackProgressLayerId,
      'line-color',
      pastColorExpr
    );
  }
  if (this.map.getLayer(this.trackPlaybackLayerId)) {
    this.map.setPaintProperty(this.trackPlaybackLayerId, 'circle-color', color);
  }
  if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
    this.map.setPaintProperty(
      this.trackPlaybackObservationLayerId,
      'circle-color',
      color
    );
  }
}

export function setTrackPlaybackLayer() {
  const playbackVisibility = this.props.selectedLayers.includes('Tracks')
    ? 'visible'
    : 'none';
  const playbackOutlineLayerId = this.trackPlaybackProgressLayerId + '_outline';
  const playbackColor =
    this.props.trackColor === 'blue' ? '#0096FF' : '#FFAB00';

  if (!this.map.getLayer(playbackOutlineLayerId)) {
    this.map.addLayer({
      id: playbackOutlineLayerId,
      source: this.trackPlaybackProgressSourceId,
      type: 'line',
      layout: {
        visibility: playbackVisibility,
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': 'rgba(0,0,0,0.6)',
        'line-width': 6,
        'line-opacity': 1
      }
    });
  }

  if (!this.map.getLayer(this.trackPlaybackProgressLayerId)) {
    this.map.addLayer({
      id: this.trackPlaybackProgressLayerId,
      source: this.trackPlaybackProgressSourceId,
      type: 'line',
      layout: {
        visibility: playbackVisibility,
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': [
          'match',
          ['get', 'progressState'],
          'past',
          playbackColor,
          '#b8bec7'
        ],
        'line-width': 3,
        'line-opacity': 1
      }
    });
  }

  if (!this.map.getLayer(this.trackPlaybackLayerId)) {
    this.map.addLayer({
      id: this.trackPlaybackLayerId,
      source: this.trackPlaybackSourceId,
      type: 'circle',
      layout: { visibility: playbackVisibility },
      paint: {
        'circle-radius': 7,
        'circle-color': playbackColor,
        'circle-opacity': 0.95,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#000000'
      }
    });
  }

  if (!this.map.getLayer(this.trackPlaybackObservationLayerId)) {
    this.map.addLayer({
      id: this.trackPlaybackObservationLayerId,
      source: this.trackPlaybackObservationSourceId,
      type: 'circle',
      layout: {
        visibility: this.props.selectedLayers.includes('Tracks')
          ? 'visible'
          : 'none'
      },
      paint: {
        'circle-radius': 10,
        'circle-color': playbackColor,
        'circle-opacity': 0.95,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 1
      }
    });
  }

  if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
    this.map.moveLayer(this.trackPlaybackProgressLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
    this.map.moveLayer(this.trackPlaybackObservationLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackLayerId)) {
    this.map.moveLayer(this.trackPlaybackLayerId);
  }
}

export function setGeoJsonLayer(mapLayer) {
  if (mapLayer.geometryType === 'MultiPolygon') {
    let color = '';
    if (mapLayer.name === 'BHS None') {
      color = '#225C8D';
    } else if (mapLayer.name === 'BHS Low') {
      color = '#FB8C24';
    } else if (mapLayer.name === 'BHS Medium') {
      color = '#FDD835';
    } else if (mapLayer.name === 'BHS High') {
      color = '#7CB342';
    } else if (mapLayer.name === 'phase 1-2') {
      color = '#1BACC1';
    } else if (mapLayer.name === 'phase 3') {
      color = '#D81B60';
    } else if (mapLayer.name === 'Corridors') {
      color = '#225C8D';
    } else {
      color =
        '#' +
        Math.floor(Math.random() * 16777215)
          .toString(16)
          .padStart(6, '0');
    }

    this.map.addLayer({
      id: mapLayer.name,
      source: mapLayer.name,
      type: 'fill',
      layout: {
        visibility: this.props.selectedLayers.includes(mapLayer.name)
          ? 'visible'
          : 'none'
      },
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], color],
        'fill-opacity': 0.25,
        'fill-outline-color': '#fff'
      }
    });

    this.setLayerClickListener(mapLayer);
  }

  if (mapLayer.geometryType === 'Polygon') {
    // let color = "#FF0000"
    let color =
      '#' +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, '0');

    this.map.addLayer({
      id: mapLayer.name,
      source: mapLayer.name,
      type: 'fill',
      layout: {
        visibility: this.props.selectedLayers.includes(mapLayer.name)
          ? 'visible'
          : 'none'
      },
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], color],
        'fill-opacity': 0.25,
        'fill-outline-color': '#fff'
      }
    });

    this.setLayerClickListener(mapLayer);
  }

  if (
    mapLayer.geometryType === 'MultiLineString' ||
    mapLayer.geometryType === 'LineString'
  ) {
    let color = '';
    if (mapLayer.name === 'roads ph. 1') {
      color = '#76CDD9';
    } else if (mapLayer.name === 'roads ph. 2') {
      color = '#E285A9';
    } else {
      color = '#004D40';
    }
    this.map.addLayer({
      id: mapLayer.name,
      source: mapLayer.name,
      type: 'line',
      layout: {
        visibility: this.props.selectedLayers.includes(mapLayer.name)
          ? 'visible'
          : 'none',
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], color],
        'line-width': 3
      }
    });

    this.setLayerClickListener(mapLayer);
  }

  if (mapLayer.geometryType === 'Point') {
    this.map.addLayer({
      id: mapLayer.name,
      source: mapLayer.name,
      type: 'symbol',
      layout: {
        visibility: this.props.selectedLayers.includes(mapLayer.name)
          ? 'visible'
          : 'none',
        'icon-image': 'infrastructure',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.05, 22, 0.5],
        'icon-padding': 0,
        'symbol-avoid-edges': true,
        'icon-allow-overlap': true
      },
      paint: {}
    });

    this.setLayerClickListener(mapLayer);
  }

  if (mapLayer.features) {
    let data = {
      type: mapLayer.type,
      features: mapLayer.features
    };

    if (this.map.getSource(mapLayer.name))
      this.map.getSource(mapLayer.name).setData(data);
  }

  this.map.getStyle().layers.forEach(layer => {
    if (layer.type === 'symbol' || layer.type === 'line') {
      this.map.moveLayer(layer.id);
    }
  });

  if (this.map.getLayer('actualTracks')) {
    this.map.moveLayer('actualTracks');
  }
  if (this.map.getLayer('actualTracks_outline')) {
    this.map.moveLayer('actualTracks_outline', 'actualTracks');
  }
  if (this.map.getLayer(this.trackDirectionLayerId)) {
    this.map.moveLayer(this.trackDirectionLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
    this.map.moveLayer(this.trackPlaybackProgressLayerId + '_outline');
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
    this.map.moveLayer(this.trackPlaybackProgressLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
    this.map.moveLayer(
      this.trackPlaybackProgressLayerId + '_outline',
      this.trackPlaybackProgressLayerId
    );
  }
  if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
    this.map.moveLayer(this.trackPlaybackObservationLayerId);
  }
  if (this.map.getLayer(this.trackPlaybackLayerId)) {
    this.map.moveLayer(this.trackPlaybackLayerId);
  }
  this.bringTrackEndpointsToFront();
  if (this.map.getLayer('Tracks')) {
    this.map.moveLayer('Tracks');
  }
  if (this.map.getLayer('Observations')) {
    this.map.moveLayer('Observations');
  }
  if (this.map.getLayer('Agents')) {
    this.map.moveLayer('Agents');
  }
  if (this.map.getLayer('Agents_zoom_highlight')) {
    this.map.moveLayer('Agents_zoom_highlight');
  }
  this.bringTrackEndpointsToFront();
}

export function setLayerClickListener(mapLayer) {
  this.map.on('click', mapLayer.name, e => {
    let f = this.map.queryRenderedFeatures(e.point);
    if (f.length && f[0].layer.source === mapLayer.name) {
      new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(
          `${
            e.features[0].properties.MapLabel
              ? e.features[0].properties.MapLabel
              : e.features[0].properties.field_7
              ? e.features[0].properties.field_7
              : e.features[0].properties.Location
              ? e.features[0].properties.Location
              : e.features[0].properties.NAME
              ? e.features[0].properties.NAME
              : 'Unknown'
          }`
        )
        .addTo(this.map);
    }
  });

  this.map.on('mouseenter', mapLayer.name, () => {
    this.map.getCanvas().style.cursor = 'pointer';
  });

  this.map.on('mouseleave', mapLayer.name, () => {
    this.map.getCanvas().style.cursor = '';
  });
}

export async function setPersonsLayer(mapLayer) {
  this.map.addLayer({
    id: mapLayer.name,
    source: mapLayer.name,
    type: 'symbol',
    filter: ['!', ['has', 'point_count']],
    layout: {
      visibility: 'visible',
      'icon-image': this.getAgentIconImageExpression(),
      'icon-size': ['interpolate', ['linear'], ['zoom'], 1, 0.05, 22, 0.5],
      'icon-padding': 0,
      'symbol-avoid-edges': true,
      'icon-allow-overlap': true,
      'text-field': this.props.showAgentNames ? AGENT_LABEL_TEXT_FIELD : '',
      'text-size': 11,
      'text-offset': [0, 1.3],
      'text-anchor': 'top',
      'text-optional': true,
      'text-allow-overlap': true
      // "pulsing-red-dot"
    }
  });

  if (this.map.getLayer(mapLayer.name)) {
    this.map.moveLayer(mapLayer.name);
  }
  this.ensureZoomToAgentOverlayLayer();
  this.updateZoomToAgentOverlay();

  if (!this.state.agentClickSet) {
    this.map.on('click', mapLayer.name, e => {
      let f = this.map.queryRenderedFeatures(e.point);
      if (f.length && f[0].layer.source === mapLayer.name) {
        var id = e.features[0].properties.EntityId;

        // Check if is already in timeline
        if (
          this.props.selectedItem.properties &&
          this.props.selectedItem.properties.EntityId === id
        ) {
          this.props.clearSelectedItem();
        } else {
          this.props.setSelectedItem(e.features[0]);
          this.props.clearEntireTrack();
        }
      }
    });

    this.setState({ agentClickSet: true });
  }

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  this.map.on('mouseenter', mapLayer.name, e => {
    this.map.getCanvas().style.cursor = 'pointer';

    const coordinates = e.features[0].geometry.coordinates.slice();
    const name = getFeatureAgentLabel(e.features[0]) || 'Unknown';
    const lastSeen = e.features[0].properties.LastSeenDateTime;
    const lastSeenDate = lastSeen ? moment(lastSeen) : null;
    const lastSeenLabel =
      lastSeenDate && lastSeenDate.isValid() && lastSeenDate.isBefore(moment())
        ? lastSeenDate.format('DD MMM YYYY - HH:mm')
        : '';

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    popup
      .setLngLat(coordinates)
      .setHTML(
        `<div><div>${name}</div>${
          lastSeenLabel
            ? `<div style="font-size: 12px; color: #6E757C;">Last seen ${lastSeenLabel}</div>`
            : ''
        }</div>`
      )
      .addTo(this.map);
  });

  this.map.on('mouseleave', mapLayer.name, () => {
    this.map.getCanvas().style.cursor = '';
    popup.remove();
  });

  // if (this.props.selectedLayers.includes(mapLayer.name)) {
  //   // Get the latest layer data
  //   this.createQuery(false, false, false, false, true);
  // }
}

export function setMediaLayer(mapLayer) {
  this.map.addLayer({
    id: mapLayer.name,
    source: mapLayer.name,
    type: 'circle',
    paint: {
      'circle-radius': 5,
      'circle-color': 'green',
      'circle-opacity': 1
    }
  });
}

export async function toggleMapLayer(clickedLayer) {
  const togglingLayerOn = !this.props.selectedLayers.includes(clickedLayer);
  const originalLayerName = clickedLayer;
  const refreshLayerData = layerName => {
    const mapLayer = (this.props.mapLayers || []).find(
      layer => layer && layer.name === layerName
    );
    if (!mapLayer) return;
    this.setState(prevState => ({
      loadingMapLayers: {
        ...prevState.loadingMapLayers,
        [layerName]: true
      }
    }));
    Promise.resolve(this.props.getSpecificLayer(mapLayer)).finally(() => {
      this.setState(prevState => ({
        loadingMapLayers: {
          ...prevState.loadingMapLayers,
          [layerName]: false
        }
      }));
    });
  };

  if (togglingLayerOn) {
    this.props.setSelectedLayers([...this.props.selectedLayers, clickedLayer]);
    refreshLayerData(originalLayerName);
  } else {
    this.props.setSelectedLayers([
      ...this.props.selectedLayers.filter(item => item !== clickedLayer)
    ]);
    this.setState(prevState => ({
      loadingMapLayers: {
        ...prevState.loadingMapLayers,
        [originalLayerName]: false
      }
    }));
  }

  if (this.props.alertTimelineOpen) {
    if (clickedLayer === 'Observations') {
      clickedLayer = 'alert';
    } else if (clickedLayer === 'alert') {
      clickedLayer = 'Observations';
    }
  }

  // Check if the layer exists
  let layerExists = this.map.getLayer(clickedLayer);
  if (typeof layerExists !== 'undefined') {
    // Check visibility and set accordingly
    let visibility = this.map.getLayoutProperty(clickedLayer, 'visibility');
    if (visibility === 'visible') {
      const selectedItem = this.props.selectedItem || {};
      const selectedIsObservation = Boolean(selectedItem.observationId);
      const selectedIsTrack = Boolean(selectedItem.featureId);
      const selectedIsAgent = Boolean(
        selectedItem &&
          selectedItem.properties &&
          selectedItem.properties.EntityType === 'user'
      );
      if (this.props.alertTimelineOpen) {
        this.map.setLayoutProperty('alert', 'visibility', 'none');
      } else {
        this.map.setLayoutProperty(clickedLayer, 'visibility', 'none');
        this.setClusterLayerVisibility(clickedLayer, 'none');
        if (clickedLayer === 'Observations') {
          this.setObservationMarkerLayerVisibility('none');
          this.clearObservationSpider();
        }
        if (
          clickedLayer === 'Agents' &&
          this.map.getLayer('Agents_zoom_highlight')
        ) {
          this.map.setLayoutProperty(
            'Agents_zoom_highlight',
            'visibility',
            'none'
          );
        }
      }
      if (clickedLayer === 'Tracks') {
        if (this.map.getLayer('Tracks_lines')) {
          this.map.setLayoutProperty('Tracks_lines', 'visibility', 'none');
        }
        if (this.map.getLayer('Tracks_lines_colour')) {
          this.map.setLayoutProperty(
            'Tracks_lines_colour',
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer('Tracks_lines_colour_outline')) {
          this.map.setLayoutProperty(
            'Tracks_lines_colour_outline',
            'visibility',
            'none'
          );
        }
        this.map.setLayoutProperty('actualTracks', 'visibility', 'none');
        if (this.map.getLayer('actualTracks_outline')) {
          this.map.setLayoutProperty(
            'actualTracks_outline',
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer('actualTracks_points')) {
          this.map.setLayoutProperty(
            'actualTracks_points',
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackDirectionLayerId)) {
          this.map.setLayoutProperty(
            this.trackDirectionLayerId,
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackEndpointsLayerId)) {
          this.map.setLayoutProperty(
            this.trackEndpointsLayerId,
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackPlaybackLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackLayerId,
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
          this.map.setLayoutProperty(
            this.trackPlaybackProgressLayerId + '_outline',
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackProgressLayerId,
            'visibility',
            'none'
          );
        }
        if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackObservationLayerId,
            'visibility',
            'none'
          );
        }
        if (this.stopTrackPlayback) {
          this.stopTrackPlayback('stopped');
        }
        this.props.clearEntireTrack();
      }
      const shouldClearSelectedItem =
        (clickedLayer === 'Observations' && selectedIsObservation) ||
        (clickedLayer === 'Tracks' && selectedIsTrack) ||
        (clickedLayer === 'Agents' && selectedIsAgent);
      if (shouldClearSelectedItem) {
        this.props.clearSelectedItem();
      }
    } else {
      if (this.props.alertTimelineOpen) {
        this.map.setLayoutProperty('alert', 'visibility', 'visible');
      } else {
        this.map.setLayoutProperty(clickedLayer, 'visibility', 'visible');
        this.setClusterLayerVisibility(clickedLayer, 'visible');
        if (clickedLayer === 'Observations') {
          this.setObservationMarkerLayerVisibility('visible');
        }
        if (
          clickedLayer === 'Agents' &&
          this.map.getLayer('Agents_zoom_highlight')
        ) {
          this.map.setLayoutProperty(
            'Agents_zoom_highlight',
            'visibility',
            'visible'
          );
          this.map.moveLayer('Agents_zoom_highlight');
        }
      }

      if (clickedLayer === 'Tracks') {
        // The generic branch above just set the icon layer ('Tracks')
        // unconditionally visible; correct that for the active display
        // mode (centerpoints|full|heatmap are mutually exclusive).
        const trackMode = this.getTrackDisplayMode
          ? this.getTrackDisplayMode()
          : 'centerpoints';
        this.map.setLayoutProperty(
          'Tracks',
          'visibility',
          trackMode === 'centerpoints' ? 'visible' : 'none'
        );
        if (this.map.getLayer('Tracks_clicked')) {
          this.map.setLayoutProperty(
            'Tracks_clicked',
            'visibility',
            trackMode === 'centerpoints' ? 'visible' : 'none'
          );
        }
        if (this.map.getLayer('Tracks_lines')) {
          this.map.setLayoutProperty(
            'Tracks_lines',
            'visibility',
            trackMode === 'full' ? 'visible' : 'none'
          );
          if (trackMode === 'full' && this.refreshFullTrackTraces) {
            this.refreshFullTrackTraces();
          }
        }
        if (this.map.getLayer('Tracks_lines_colour')) {
          this.map.setLayoutProperty(
            'Tracks_lines_colour',
            'visibility',
            trackMode === 'fullColour' ? 'visible' : 'none'
          );
          if (trackMode === 'fullColour' && this.refreshFullTrackTraces) {
            this.refreshFullTrackTraces();
          }
        }
        if (this.map.getLayer('Tracks_lines_colour_outline')) {
          this.map.setLayoutProperty(
            'Tracks_lines_colour_outline',
            'visibility',
            trackMode === 'fullColour' ? 'visible' : 'none'
          );
        }
        this.map.setLayoutProperty('actualTracks', 'visibility', 'visible');
        if (this.map.getLayer('actualTracks_outline')) {
          this.map.setLayoutProperty(
            'actualTracks_outline',
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer('actualTracks_points')) {
          this.map.setLayoutProperty(
            'actualTracks_points',
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer(this.trackDirectionLayerId)) {
          this.map.setLayoutProperty(
            this.trackDirectionLayerId,
            'visibility',
            // Colour-coded mode splits each track into many short segments
            // (see actualTracks.js) - keep direction arrows hidden for it,
            // same as setActualTrackLayer's initial trackDirectionVisibility.
            this.props.trackColor === 'colourCoded' ? 'none' : 'visible'
          );
        }
        if (this.map.getLayer(this.trackEndpointsLayerId)) {
          this.map.setLayoutProperty(
            this.trackEndpointsLayerId,
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer(this.trackPlaybackLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackLayerId,
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer(this.trackPlaybackProgressLayerId + '_outline')) {
          this.map.setLayoutProperty(
            this.trackPlaybackProgressLayerId + '_outline',
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer(this.trackPlaybackProgressLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackProgressLayerId,
            'visibility',
            'visible'
          );
        }
        if (this.map.getLayer(this.trackPlaybackObservationLayerId)) {
          this.map.setLayoutProperty(
            this.trackPlaybackObservationLayerId,
            'visibility',
            'visible'
          );
        }
        this.bringTrackEndpointsToFront();
      }
    }
  } else {
    // Layer does not yet exist (e.g. style reload race). Keep toggle as UI-only.
  }
}
